import moment from 'moment';

const WEEKDAYS: Record<string, number> = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
};

const DEFAULT_START_HOUR = 9;

// upper bound on duration offsets (60 days) — anything larger is
// treated as a typo/garbage link and falls back to the default start date
const MAX_OFFSET = { hours: 1440, days: 60 } as const;

/**
 * Next calendar occurrence of an ISO weekday (1=Monday..7=Sunday) at the given
 * hour, strictly in the future — if `from` already falls on that weekday, jumps
 * a full week ahead rather than returning today. Shared by `parseStartOffset`
 * below and the one-way quick-search cards (`handleQuickSearch` in
 * VehicleSearch.tsx), which both need "next Tuesday at 9" style arithmetic.
 */
export function nextWeekday(isoWeekday: number, from: moment.Moment = moment(), hour = DEFAULT_START_HOUR): moment.Moment {
    const daysUntil = ((isoWeekday - from.isoWeekday() + 7) % 7) || 7;
    return from.clone().add(daysUntil, 'days').hours(hour).minutes(0).seconds(0).milliseconds(0);
}

/**
 * Rounds up to the next :00/:15/:30/:45 slot, dropping seconds and
 * milliseconds. A moment already exactly on a slot is left unchanged.
 */
function roundUpToQuarterHour(m: moment.Moment): moment.Moment {
    const remainder = m.minutes() % 15;
    if (remainder || m.seconds() || m.milliseconds()) {
        m.add(15 - remainder, 'minutes');
    }
    return m.seconds(0).milliseconds(0);
}

/**
 * Parses the `startoffset` URL param into a start moment — an alternative to
 * absolute `startdate`/`enddate` for evergreen marketing links ("book for next
 * Monday", "72 hours from now") that would otherwise go stale. Accepts a
 * duration shorthand (`72h`, `3d`) measured from the moment the page loads, or
 * a weekday name (`monday`..`sunday`) resolved to its next occurrence at the
 * default 09:00 start time. Durations are rounded up to the next 15-minute
 * slot to match the time pickers' `minutesStep`, and must be between 1 and
 * `MAX_OFFSET` (60d / 1440h) — a zero offset would resolve to "now" and already
 * be in the past on submit, and a huge one can overflow to an invalid date.
 * Returns null for anything else so the caller falls back to the normal
 * default start date.
 */
export function parseStartOffset(value: string | null | undefined): moment.Moment | null {
    if (!value) return null;
    const trimmed = value.trim().toLowerCase();

    const duration = trimmed.match(/^(\d+)(h|d)$/);
    if (duration) {
        const amount = parseInt(duration[1], 10);
        const unit = duration[2] === 'h' ? 'hours' : 'days';
        if (amount < 1 || amount > MAX_OFFSET[unit]) return null;
        const start = roundUpToQuarterHour(moment().add(amount, unit));
        return start.isValid() ? start : null;
    }

    const isoWeekday = WEEKDAYS[trimmed];
    if (isoWeekday) {
        return nextWeekday(isoWeekday);
    }

    return null;
}
